/**
 * MARK:- Cart zone sync (#2899)
 * The single serialized owner of cart line zone properties AND every Plus (_custom_price) write.
 *
 * window.NHCartZoneSync = { schedule, run, price, renderBadges }
 *   schedule()     coalesces triggers into one promise queue (a request mid-pass runs once more);
 *                 deferred re-runs are capped at MAX_RERUNS per external trigger
 *   run()          one pass, see below
 *   price()        Plus price in dollars from a fresh /cart.js and the row verdicts
 *   renderBadges() re-judges the cart line zone badges in place (drawer and cart page)
 *
 * Triggers: DOMContentLoaded, nhzone:changed, on:cart:add, on:line-item:change,
 * on:cart-items:refresh, and a debounced (150 ms) MutationObserver for re-rendered
 * cart-items (quick-add renders the drawer in a 700 ms timeout and emits no cart event).
 *
 * A pass:
 *   1. zone = NHZone.currentZone()
 *   2. defer while cart-items.pointer-events-none exists (the theme's in-flight marker)
 *   3. fetch /cart.js and judge every plant line from its row facts (keyed by variant id)
 *   4. write the desired property set line by line. Probe 2026-10-01 (Branch Q): a
 *      property-only /cart/change.js resets quantity to 1, so each write re-reads /cart.js,
 *      finds the line by key (gone: skip and re-run) and sends the fresh quantity. A
 *      quantity change inside the sub-second read-to-write window is a known limitation.
 *   5. Plus from the same fresh /cart.js and verdict map: k = Plus-eligible lines; k = 0
 *      removes the warranty line, otherwise _custom_price is repriced when it differs.
 *   6. after any write: the drawer refresh, or on /cart (no drawer) cart-items.refresh().
 * Write guard: MAX_WRITE_PASSES consecutive writing passes halt writes until a shopper action;
 * the re-render in step 6 never re-arms them, so a write that never converges cannot loop.
 */
(function () {
  'use strict';

  if (window.NHCartZoneSync) return;

  const ROW_SELECTOR = '.cart-item[data-line-key]';
  const MAX_WRITE_PASSES = 4;
  const MAX_RERUNS = 10;

  let running = null;
  let again = false;
  let writePasses = 0;
  let reruns = 0;
  let retryTimer = null;
  let lastRefreshZone = null;
  // MARK:- Tripped write guard: no more writes until a shopper action (zone change, add, quantity
  // change, or a direct schedule() call). Our own re-render fires the MutationObserver and
  // on:cart-items:refresh, so those alone never re-arm writes; a changed set of rows (quick-add
  // emits no cart event) does.
  let writesHalted = false;
  let haltedRows = '';

  function rowSignature() {
    return Array.from(document.querySelectorAll(ROW_SELECTOR), (row) => row.dataset.variantId).sort().join(',');
  }

  // MARK:- Helpers
  function parseJson(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  async function getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  async function change(body) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`cart/change.js ${response.status}`);
    return response.json();
  }

  function inFlight() {
    return Boolean(document.querySelector('cart-items.pointer-events-none'));
  }

  function warrantyConfig() {
    const container = document.querySelector('[data-warranty-variant-id]');
    const warrantyRow = document.querySelector('.cart-item--warranty[data-variant-id]');
    const variantId = Number((container && container.dataset.warrantyVariantId) || (warrantyRow && warrantyRow.dataset.variantId)) || null;
    const pct = container ? parseFloat(container.dataset.warrantyPercentage) : NaN;
    return { variantId, pct: Number.isFinite(pct) ? pct : null };
  }

  // MARK:- Row facts keyed by variant id (facts are product and variant level, so a key change after a write is harmless)
  function readFacts() {
    const facts = new Map();
    document.querySelectorAll(ROW_SELECTOR).forEach((row) => {
      if (row.classList.contains('cart-item--warranty')) return;
      const variantId = Number(row.dataset.variantId);
      if (!variantId || facts.has(variantId)) return;
      facts.set(variantId, {
        zoneRange: row.dataset.zoneRange || '',
        zoneList: parseJson(row.dataset.zoneList, []),
        productType: row.dataset.productType || '',
        tags: parseJson(row.dataset.productTags, []),
        warrantable: row.dataset.warrantable === 'true'
      });
    });
    return facts;
  }

  // MARK:- Verdict per line key; null when a plant line has no rendered row yet (DOM still catching up)
  function verdictMap(cart, facts, zone, warrantyVariantId) {
    const verdicts = {};
    for (const item of cart.items) {
      if (item.variant_id === warrantyVariantId) continue;
      const f = facts.get(item.variant_id);
      if (!f) return null;
      verdicts[item.key] = { warrantable: f.warrantable, verdict: window.NHZone.judge(f, zone).verdict };
    }
    return verdicts;
  }

  // MARK:- Badge markup, identical to snippets/custom-cart-zone-badge.liquid
  function badgeHtml(result) {
    const zone = result.customerZoneNumber;
    if (result.verdict === 'out_of_zone') {
      return `<details class="cart-item__zone-badge cart-item__zone-badge--out">`
        + `<summary><span>Outside zone ${zone}: not guaranteed</span>`
        + `<svg class="cart-item__zone-badge-chevron" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></summary>`
        + `<div class="cart-item__zone-badge-body">Rated for zones ${result.min} to ${result.max}. Not covered by the guarantee. `
        + `<button type="button" class="cart-item__zone-badge-link" onclick="toggleGrowZoneDrawer(event)">Change zone</button></div>`
        + `</details>`;
    }
    if (result.verdict === 'in_zone') {
      return `<div class="cart-item__zone-badge cart-item__zone-badge--in">Grows in zone ${zone}</div>`;
    }
    return '';
  }

  // MARK:- Pill icons, identical to snippets/cart-items.liquid
  const PILL_ICON = {
    covered: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="currentColor"/><path d="M5 8.5 L7 10.5 L11 6" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    out_of_zone: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><circle cx="8" cy="8" r="7.25" stroke="currentColor" stroke-width="1.5" fill="none"/><line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
  };

  // MARK:- Plus pill on a warrantable line (rendered only when Plus is in the cart): flip it with the zone badge
  function renderWarrantyPill(row, result) {
    const pill = row.querySelector('.cart-item__warranty-badge[data-warranty-pill]');
    if (!pill) return;
    const want = window.NHZone.warrantyPill(result.verdict);
    if (pill.dataset.warrantyPill === want.state) return;
    pill.dataset.warrantyPill = want.state;
    pill.classList.toggle('cart-item__warranty-badge--covered', want.state === 'covered');
    pill.classList.toggle('cart-item__warranty-badge--not-covered', want.state !== 'covered');
    pill.innerHTML = `${PILL_ICON[want.state]}<span>${want.text}</span>`;
  }

  function renderBadges(zone) {
    if (!window.NHZone) return;
    const shopperZone = zone === undefined ? window.NHZone.currentZone() : zone;
    document.querySelectorAll(ROW_SELECTOR).forEach((row) => {
      if (row.classList.contains('cart-item--warranty')) return;
      const container = row.querySelector('.cart-item__restriction-badge-container');
      if (!container) return;
      const existing = container.querySelector('.cart-item__zone-badge');
      const result = window.NHZone.judge({
        zoneRange: row.dataset.zoneRange || '',
        zoneList: parseJson(row.dataset.zoneList, []),
        productType: row.dataset.productType || '',
        tags: parseJson(row.dataset.productTags, [])
      }, shopperZone);
      renderWarrantyPill(row, result);
      // MARK:- Red restriction badges win; a restricted line shows no zone badge
      if (container.querySelector('.cart-item__restriction-badge')) {
        if (existing) existing.remove();
        return;
      }
      const html = badgeHtml(result);
      const key = `${result.verdict}|${result.customerZoneNumber}|${result.min}|${result.max}`;
      if (existing && existing.dataset.zoneBadgeKey === key) return;
      if (existing) existing.remove();
      if (!html) return;
      container.insertAdjacentHTML('beforeend', html);
      container.querySelector('.cart-item__zone-badge').dataset.zoneBadgeKey = key;
    });
  }

  // MARK:- Plus price from a fresh /cart.js and the row verdicts (custom.js add path).
  // null when any plant line has no rendered row or the percentage is unknown: the caller must
  // never write a 0 or missing _custom_price, so it refuses the add instead.
  async function price() {
    if (!window.NHZone) return null;
    const cart = await getCart();
    const { variantId, pct } = warrantyConfig();
    if (pct === null) return null;
    const verdicts = verdictMap(cart, readFacts(), window.NHZone.currentZone(), variantId);
    if (!verdicts) return null;
    return window.NHZone.plusPrice(cart, verdicts, pct);
  }

  async function refreshCart() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      return;
    }
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') await cartItems.refresh();
  }

  // MARK:- Deferred re-run (in-flight DOM or a line without a row yet), capped per trigger.
  // Any external trigger (event, mutation, custom.js) resets the count via schedule(true).
  function deferPass() {
    clearTimeout(retryTimer);
    if (reruns >= MAX_RERUNS) {
      console.warn('NHCartZoneSync: stopped after repeated reruns');
      return;
    }
    reruns += 1;
    retryTimer = setTimeout(() => schedule(false), 500);
  }

  // MARK:- One pass
  async function run() {
    if (!window.NHZone) return;
    const zone = window.NHZone.currentZone();
    renderBadges(zone);
    if (writesHalted && rowSignature() === haltedRows) return;
    writesHalted = false;
    if (!document.querySelector(ROW_SELECTOR)) return;
    if (inFlight()) {
      deferPass();
      return;
    }

    const facts = readFacts();
    const { variantId: warrantyVariantId, pct } = warrantyConfig();
    let cart = await getCart();
    let wrote = false;
    let rerun = false;

    // MARK:- Step 4: zone properties, sequential (a property change rewrites item.key)
    const pending = [];
    for (const item of cart.items) {
      if (item.variant_id === warrantyVariantId) continue;
      const f = facts.get(item.variant_id);
      if (!f) {
        rerun = true;
        continue;
      }
      const verdict = window.NHZone.judge(f, zone).verdict;
      const desired = window.NHZone.desiredProperties(item.properties, verdict, zone);
      if (window.NHZone.needsWrite(item.properties, desired)) pending.push({ key: item.key, desired });
    }

    // MARK:- One failed line (e.g. a 422) never skips the remaining lines or step 5
    let deferred = false;
    for (const write of pending) {
      if (inFlight()) {
        deferred = true;
        break;
      }
      try {
        const fresh = await getCart();
        const line = fresh.items.find((item) => item.key === write.key);
        if (!line) {
          rerun = true;
          continue;
        }
        await change({ id: write.key, quantity: line.quantity, properties: write.desired });
        wrote = true;
      } catch (error) {
        console.error('NHCartZoneSync: line write failed', write.key, error);
      }
    }
    if (deferred) rerun = true;

    // MARK:- Step 5: Plus, from the same fresh /cart.js and verdict map as k. Always runs after step 4.
    if (warrantyVariantId) {
      try {
        cart = wrote ? await getCart() : cart;
        const verdicts = verdictMap(cart, readFacts(), zone, warrantyVariantId);
        const warrantyLine = cart.items.find((item) => item.variant_id === warrantyVariantId);
        if (warrantyLine && verdicts && !inFlight()) {
          const k = Object.values(verdicts).filter((v) => window.NHZone.plusEligible(v)).length;
          if (k === 0) {
            await change({ id: warrantyLine.key, quantity: 0 });
            wrote = true;
          } else if (pct !== null) {
            const plus = window.NHZone.plusPrice(cart, verdicts, pct);
            const props = warrantyLine.properties || {};
            if (String(props._custom_price) !== plus || String(props._percentage_value) !== String(pct) || warrantyLine.quantity !== 1) {
              await change({
                id: warrantyLine.key,
                quantity: 1,
                properties: Object.assign({}, props, { _custom_price: plus, _percentage_value: pct })
              });
              wrote = true;
            }
          }
        } else if (warrantyLine && !verdicts) {
          rerun = true;
        }
      } catch (error) {
        console.error('NHCartZoneSync: Plus write failed', error);
      }
    }

    // MARK:- Step 6: re-render after writes, or once when the rendered zone is stale (guarantee copy is Liquid)
    const cartItems = document.querySelector('cart-items');
    const renderedZone = cartItems ? cartItems.dataset.zone || '' : '';
    const zoneStale = window.NHZone.zoneLabel(renderedZone) !== window.NHZone.zoneLabel(zone) && lastRefreshZone !== zone;
    if (wrote || zoneStale) {
      lastRefreshZone = zone;
      await refreshCart();
    }

    if (wrote) {
      writePasses += 1;
    } else {
      writePasses = 0;
    }
    if (rerun) deferPass();
  }

  function schedule(trigger) {
    if (trigger !== false) reruns = 0;
    if (trigger === 'shopper') writesHalted = false;
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      // MARK:- Yield first: a pass that breaks on the guard without awaiting would otherwise clear
      // `running` before this assignment lands, leaving a settled promise that swallows every later trigger
      await Promise.resolve();
      do {
        again = false;
        if (writePasses >= MAX_WRITE_PASSES) {
          // MARK:- Guard: stop a write loop (each pass should converge in one extra pass)
          console.warn('NHCartZoneSync: stopped after repeated writes');
          writePasses = 0;
          writesHalted = true;
          haltedRows = rowSignature();
          break;
        }
        try {
          await run();
        } catch (error) {
          console.error('NHCartZoneSync:', error);
        }
      } while (again);
      running = null;
    })();
    return running;
  }

  // MARK:- Re-rendered cart rows: debounce DOM additions that carry cart-items or a cart row
  let mutationTimer = null;
  function touchesCart(node) {
    return node.nodeType === 1 && (node.matches('cart-items, .cart-item') || node.querySelector('cart-items, .cart-item'));
  }
  const observer = new MutationObserver((records) => {
    const relevant = records.some((record) => Array.prototype.some.call(record.addedNodes, touchesCart));
    if (!relevant) return;
    clearTimeout(mutationTimer);
    mutationTimer = setTimeout(schedule, 150);
  });

  function init() {
    observer.observe(document.body, { childList: true, subtree: true });
    ['nhzone:changed', 'on:cart:add', 'on:line-item:change'].forEach((name) => {
      document.addEventListener(name, () => schedule('shopper'));
    });
    document.addEventListener('on:cart-items:refresh', () => schedule());
    schedule();
  }

  window.NHCartZoneSync = { schedule: () => schedule('shopper'), run, price, renderBadges };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
